/*
  © 2024–2026 Atlas PHM
  Independently developed software. All rights reserved.
*/

/**
 * Technical Appendix Page
 *
 * Conditionally rendered technical diagrams based on recommended option:
 * - Laminar Flow Filter → Cyclonic Physics diagram
 * - S-Plan Topology → System Layout diagram
 * - Condensing Modulation → Modulation vs Cycling diagram
 *
 * Design philosophy:
 * - Keep main recommendation page clean
 * - Provide technical depth for those who want it
 * - High-fidelity diagrams explain the "how" behind recommendations
 */

import type { TechnicalAppendixData, TechnicalDiagramData } from '@core/presentationPrint';
import { LaminarFlowSVG } from '@system-rec/decision-ui';
import { PipeworkFlowChart } from './PipeworkFlowChart';
import { ModulationCyclingChart } from './ModulationCyclingChart';

interface TechnicalAppendixPageProps {
  data: TechnicalAppendixData;
}

/**
 * Render diagram based on type
 */
function TechnicalDiagram({ diagram }: { diagram: TechnicalDiagramData }) {
  return (
    <div className="dp-tech-diagram">
      <h3 className="dp-tech-diagram__title">{diagram.title}</h3>
      <p className="dp-tech-diagram__description">{diagram.description}</p>
      <div className="dp-tech-diagram__content">
        {diagram.diagramType === 'laminar-flow' && (
          <div className="dp-laminar-flow-wrapper">
            <LaminarFlowSVG />
          </div>
        )}
        {diagram.diagramType === 's-plan-topology' && (
          <div className="dp-s-plan-wrapper">
            <PipeworkFlowChart />
          </div>
        )}
        {diagram.diagramType === 'modulation-cycling' && (
          <div className="dp-modulation-wrapper">
            <ModulationCyclingChart />
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Main Technical Appendix Page component
 */
export function TechnicalAppendixPage({ data }: TechnicalAppendixPageProps) {
  return (
    <div className="dp-page dp-technical-appendix-page">
      <div className="dp-tech-header">
        <h2>{data.title}</h2>
        <p className="dp-tech-subtitle">
          Technical diagrams explaining the physics and engineering behind your recommended system
        </p>
      </div>

      <div className="dp-tech-diagrams">
        {data.diagrams.map((diagram) => (
          <TechnicalDiagram key={diagram.id} diagram={diagram} />
        ))}
      </div>

      {/* Styling for this page */}
      <style>{`
        .dp-technical-appendix-page {
          padding: 20px;
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
        }

        .dp-tech-header {
          margin-bottom: 30px;
          border-bottom: 2px solid #e2e8f0;
          padding-bottom: 16px;
        }

        .dp-tech-header h2 {
          font-size: 24px;
          font-weight: 700;
          margin-bottom: 8px;
          color: #1e293b;
        }

        .dp-tech-subtitle {
          font-size: 14px;
          color: #64748b;
          max-width: 600px;
        }

        .dp-tech-diagrams {
          display: flex;
          flex-direction: column;
          gap: 32px;
        }

        .dp-tech-diagram {
          page-break-inside: avoid;
        }

        .dp-tech-diagram__title {
          font-size: 18px;
          font-weight: 600;
          margin-bottom: 8px;
          color: #0f172a;
        }

        .dp-tech-diagram__description {
          font-size: 14px;
          color: #475569;
          margin-bottom: 16px;
          line-height: 1.5;
        }

        .dp-tech-diagram__content {
          background: white;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          padding: 16px;
          display: flex;
          justify-content: center;
          align-items: center;
        }

        .dp-laminar-flow-wrapper,
        .dp-s-plan-wrapper,
        .dp-modulation-wrapper {
          width: 100%;
          max-width: 600px;
        }

        /* Print styles */
        @media print {
          .dp-technical-appendix-page {
            page-break-before: always;
          }

          .dp-tech-diagram {
            page-break-inside: avoid;
            page-break-after: auto;
          }
        }
      `}</style>
    </div>
  );
}

export default TechnicalAppendixPage;
